<!--
Currently does not use popover API, as any polyfill would be very complicated (or maybe even impossible). So
we use fixed positioning and our own show/hide logic, which is good enough for now. If all supported browsers
implement the popover API natively, we can switch to it later on. We accept that the popover could be wobbling
when the user scrolls the page. An option to auto-close the popover when scrolling would be possible,
but is not implemented right now: Programmatically visible popovers should stay visible when the user scrolls, like
toggletips that are visible by default.

Available CSS vars, set in your parent component:
  --popover-frame-spacing: 8px default (read in utils via getComputedStyle)
  --popover-max-width: 432px default
  --popover-z-index: 9999 default
  --popover-transition-show: none default
  --popover-transition-hide: none default
-->
<script lang="ts">
  import { onMount } from "svelte";
  import {
    OnAnimationFrame,
    onElementAnimated,
    OnImmediate,
    useDomAnimationHelper,
    type AnimationHelperAwaiter,
  } from "@otto-ec/otto-components-utils/svelte/useDomAnimationHelper";
  import { calculatePopoverPosition, defer, obj2style } from "./Popover.utils";
  import { attachPositioningEventListeners } from "./Popover.events";
  import {
    PopoverPosition,
    type PopoverPositioningResult,
    type PopoverProps,
  } from "./Popover.types";

  /*                                                                    */
  const ENABLE_POPOVER_API = false;
  const ENABLE_FIXED_POSITIONING = true;

  let {
    contextElement,
    children,
    bubble: Bubble,
    show = false,
    sticky = false,
    preferredPosition,
    onFullyShown,
  }: PopoverProps = $props();

  let popoverElement: HTMLElement | null = $state(null);
  let animationElement: HTMLElement | null = $state(null);

  let positioningResult = $state<PopoverPositioningResult>({
    /*                                                         */
    /*                                                                                                                     */
    /*                                                      */
    /*                                     */
    position: preferredPosition || PopoverPosition.BOTTOM,
    anchorLeftPosition: false,
    popoverStyles: {},
  });

  let hideInProgress = $state(false);

  /*                                               */
  let initialPositioning: ReturnType<typeof defer> | null = null;
  onMount(() => {
    initialPositioning = defer();
  });

  const onPositioningDone: AnimationHelperAwaiter = (callback) => {
    initialPositioning?.promise.then(callback);
  };

  const onHide: AnimationHelperAwaiter = (callback) => {
    if (ENABLE_POPOVER_API) popoverElement?.togglePopover?.(false);
    callback();
  };

  const [visibilityState, setVisibility] = useDomAnimationHelper({
    render: [OnImmediate, OnImmediate],
    positioning: [OnAnimationFrame, onHide],
    positioningDone: [onPositioningDone, onElementAnimated(() => animationElement)],
    animate: [OnAnimationFrame, OnImmediate],
    animationDone: [onElementAnimated(() => animationElement), OnImmediate],
  });

  function handleRecalc(): void {
    if (!popoverElement || !contextElement) return;
    positioningResult = calculatePopoverPosition(
      popoverElement,
      contextElement,
      preferredPosition,
      sticky,
      ENABLE_FIXED_POSITIONING,
      ENABLE_POPOVER_API && popoverElement?.showPopover !== undefined,
    );
    if (initialPositioning?.pending) {
      initialPositioning.resolve(true);
    }
  }

  export function recalcPosition(): void {
    handleRecalc();
  }

  /*                                        */
  $effect(() => {
    if (!popoverElement || !contextElement) return;
    const destroy = attachPositioningEventListeners(popoverElement, contextElement, handleRecalc);
    return destroy;
  });

  /*                               */
  $effect(() => {
    if (!visibilityState.positioning) return;
    handleRecalc();
  });

  /*                                         */
  $effect(() => {
    setVisibility(show);
    hideInProgress = !show;
    /*                                                                        */
    if (ENABLE_POPOVER_API && show) popoverElement?.showPopover?.();
  });

  /*                                 */
  $effect(() => {
    if (visibilityState.animationDone && show) {
      onFullyShown?.();
    }
  });
</script>

{#if visibilityState.render}
  <div
    class="popover-popover"
    aria-hidden={!visibilityState.render}
    inert={!visibilityState.render}
    class:ready={visibilityState.positioning}
    class:visible={visibilityState.animate}
    class:measuring={!visibilityState.positioningDone}
    class:hideInProgress
    style={obj2style(positioningResult.popoverStyles)}
    bind:this={animationElement}
    bind:this={popoverElement}
    popover={ENABLE_POPOVER_API ? "manual" : undefined}
  >
    <Bubble
      anchorLeftPosition={positioningResult.anchorLeftPosition}
      position={positioningResult.position}
    >
      {@render children?.()}
    </Bubble>
  </div>
{/if}

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  :global {
    .popover-popover {
      display: none;
      opacity: 0;
      position: fixed;
      /*                */
      width: max-content;
      z-index: var(--popover-z-index, 9999);

      &.ready {
        display: block;
        transition: var(--popover-transition-show, none);
        opacity: 0;
      }
      &.hideInProgress {
        transition: var(--popover-transition-hide, none);
        opacity: 0;
      }

      &.visible {
        display: block;
        opacity: 1;
      }

      /*                                                                        */
      &.measuring {
        display: block;
        position: fixed;
        left: -1000px;
        top: -1000px;
        visibility: hidden;
        pointer-events: none;
        width: fit-content;
        height: fit-content;
        max-width: var(--popover-max-width, 432px);
        min-width: var(--popover-min-width, 0);
      }
    }
  }
</style>
